/**
 * Keep a Unity canvas in the document after the React player unmounts so
 * `createUnityInstance` / `Quit()` can finish against a live WebGL handle.
 *
 * Overlay close navigates immediately (close must stay snappy).
 * React would otherwise remove the canvas mid-boot, and Unity then throws
 * `GLctx` / `eventHandler.target` on `deleteContext`.
 *
 * Do not hide the parked canvas with `display: none` — that can drop the
 * GPU context in some browsers. Off-screen positioning keeps the buffer.
 */

export const UNITY_CANVAS_PARK_HOLDER_ID = "playmath-unity-canvas-park";

function getOrCreateParkHolder(): HTMLElement | null {
  if (typeof document === "undefined") return null;

  const existing = document.getElementById(UNITY_CANVAS_PARK_HOLDER_ID);
  if (existing) return existing;

  const holder = document.createElement("div");
  holder.id = UNITY_CANVAS_PARK_HOLDER_ID;
  holder.setAttribute("aria-hidden", "true");
  holder.setAttribute("data-playmath-unity-canvas-park", "true");
  holder.style.position = "fixed";
  holder.style.left = "-10000px";
  holder.style.top = "0";
  holder.style.pointerEvents = "none";
  document.body.appendChild(holder);
  return holder;
}

function removeParkHolderIfEmpty(): void {
  if (typeof document === "undefined") return;
  const holder = document.getElementById(UNITY_CANVAS_PARK_HOLDER_ID);
  if (holder && holder.childElementCount === 0) {
    holder.remove();
  }
}

/**
 * Move `canvas` out of the React tree, leaving a comment so unmount still
 * has a child to remove. Returns true when the canvas is in the park.
 */
export function parkUnityCanvas(canvas: HTMLCanvasElement | null | undefined): boolean {
  if (!canvas || typeof document === "undefined") return false;

  const holder = getOrCreateParkHolder();
  if (!holder) return false;
  if (canvas.parentNode === holder) return true;

  if (canvas.parentNode) {
    const placeholder = document.createComment("playmath-unity-canvas-park");
    canvas.parentNode.replaceChild(placeholder, canvas);
  }

  holder.appendChild(canvas);
  return true;
}

export function releaseParkedUnityCanvas(canvas: HTMLCanvasElement | null | undefined): void {
  if (!canvas) return;
  canvas.remove();
  removeParkHolderIfEmpty();
}

export function resetUnityCanvasParkForTests(): void {
  if (typeof document === "undefined") return;
  document.getElementById(UNITY_CANVAS_PARK_HOLDER_ID)?.remove();
}
